import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { errMsg, fmtRM } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/common";
import {
  CheckCircle2, XCircle, Clock3, AlertTriangle, ArrowLeft, RefreshCw,
} from "lucide-react";

/**
 * Billplz redirect landing page.
 *
 * IMPORTANT: We NEVER trust redirect query params as proof of payment.
 * The backend polls its own DB (updated by verified server-to-server callback)
 * and returns the authoritative status.
 */
export default function BillplzResultPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const attemptId = params.get("attempt");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    if (!attemptId) {
      setError("Missing payment reference");
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get(`/payments/billplz/status/${attemptId}`);
      setStatus(data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto-poll every 4s for up to 60s if still pending (callback may lag)
    let attempts = 0;
    const int = setInterval(async () => {
      attempts += 1;
      if (attempts > 15) { clearInterval(int); return; }
      const s = status;
      if (s && (s.status === "paid" || s.status === "failed"
                || s.status === "cancelled" || s.status === "expired"
                || s.status === "verification_failed")) {
        clearInterval(int); return;
      }
      setPolling(true);
      await fetchStatus();
      setPolling(false);
    }, 4000);
    return () => clearInterval(int);
  }, [attemptId]);

  if (loading) return <div className="p-8"><Spinner full /></div>;
  if (error) {
    return (
      <div className="max-w-lg mx-auto p-8 text-center" data-testid="billplz-result-error">
        <XCircle className="w-12 h-12 mx-auto text-destructive mb-3" />
        <h1 className="text-xl font-semibold mb-2">Payment result unavailable</h1>
        <p className="text-sm text-muted-foreground mb-5">{error}</p>
        <Button onClick={() => navigate("/parent/invoices")}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to invoices
        </Button>
      </div>
    );
  }

  const s = status || {};
  const config = STATE_CONFIG[s.status] || STATE_CONFIG.processing;
  const Icon = config.icon;

  return (
    <div className="max-w-lg mx-auto p-6" data-testid="billplz-result-page">
      {s.sandbox && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 font-semibold text-center"
             data-testid="sandbox-badge">
          BILLPLZ SANDBOX — TEST MODE
        </div>
      )}
      <Card>
        <CardHeader className="text-center">
          <Icon className={`w-14 h-14 mx-auto ${config.color}`} strokeWidth={1.5} />
          <CardTitle className={`text-2xl ${config.color}`} data-testid="billplz-result-title">
            {config.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center mb-5">{config.subtitle}</p>
          <div className="space-y-2 text-sm bg-muted/50 rounded-md p-4">
            <Row label="Invoice" value={s.invoice_number || "—"} />
            <Row label="Amount"  value={s.amount_rm !== undefined ? fmtRM(s.amount_rm) : "—"} />
            <Row label="Status"  value={<Badge>{s.status}</Badge>} />
            {s.receipt_number && <Row label="Receipt" value={<span className="font-mono">{s.receipt_number}</span>} />}
            {s.bill_id && <Row label="Bill ID" value={<span className="font-mono text-xs">{s.bill_id}</span>} />}
            {s.failure_reason && (
              <Row label="Reason" value={<span className="text-destructive text-xs">{s.failure_reason}</span>} />
            )}
          </div>
          <div className="flex gap-2 mt-5">
            <Button variant="outline" className="flex-1"
                    onClick={() => navigate("/parent/invoices")}
                    data-testid="billplz-back-to-invoices">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to invoices
            </Button>
            <Button variant="outline" onClick={fetchStatus}
                    disabled={polling} data-testid="billplz-refresh-status">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${polling ? "animate-spin" : ""}`} />
              {polling ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
          {s.status === "processing" || s.status === "pending_payment" ? (
            <p className="text-xs text-muted-foreground text-center mt-4">
              Waiting for bank confirmation. This page will refresh automatically.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

const STATE_CONFIG = {
  paid:               { icon: CheckCircle2, color: "text-emerald-600",
                        title: "Payment Successful",
                        subtitle: "Thank you — your invoice has been marked as paid. A receipt has been sent to your email." },
  pending_payment:    { icon: Clock3, color: "text-amber-600",
                        title: "Payment Processing",
                        subtitle: "We're waiting for confirmation from your bank. This usually takes a few seconds to a minute." },
  processing:         { icon: Clock3, color: "text-amber-600",
                        title: "Payment Processing",
                        subtitle: "We're waiting for confirmation from your bank." },
  failed:             { icon: XCircle, color: "text-destructive",
                        title: "Payment Failed",
                        subtitle: "Your payment did not go through. No amount has been charged. Please try again." },
  cancelled:          { icon: AlertTriangle, color: "text-amber-600",
                        title: "Payment Cancelled",
                        subtitle: "You cancelled the payment. Your invoice remains unpaid — you can try again anytime." },
  expired:            { icon: AlertTriangle, color: "text-slate-500",
                        title: "Bill Expired",
                        subtitle: "This payment link has expired. Please open your invoice and generate a new one." },
  verification_failed:{ icon: XCircle, color: "text-destructive",
                        title: "Verification Failed",
                        subtitle: "We could not verify this payment. Our finance team has been alerted — please contact us if you were charged." },
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right break-all">{value}</span>
  </div>
);
